/**
 * BREAK 2 — "Open in Planning ✕ Scenario bound + scope set".
 *
 * A plan cycle created from a published priority list (or an item pool /
 * collection — the same gap applies to all three, since none of them wires
 * the planning engine on their own) used to leave the engine unconfigured:
 * no scenario bound to the new cycle, no Input Scope set, so the user had to
 * do both by hand in `ScenarioList.tsx` (bind) and the engine's scope picker
 * (`PlanningEngineContext.setEngineInput`, which itself calls
 * `bindScenarioInputSource`). This composes the same service functions those
 * surfaces already use — `createScenario`, `bindScenarioInputSource`,
 * `activateScenario` (all in `planningEngineService.ts`) — into one call so
 * plan creation finishes with a ready-to-run engine instance.
 *
 * Each step is attempted in order and recorded; a failure stops the chain
 * but never rolls back what already succeeded (the plan cycle in particular
 * is never touched here — it's created by the caller first). `resume` lets
 * a retry ("Finish setup") skip whatever already completed instead of
 * re-creating a duplicate scenario.
 */
import {
  activateScenario,
  bindScenarioInputSource,
  createScenario,
  type EngineInputSource,
} from '@/lib/planningEngineService';
import { usePlanningDefaultsStore } from '@/stores/prioritization/planningDefaultsStore';

export type PlanScenarioSourceType = Extract<
  EngineInputSource['type'],
  'item_pool' | 'collection' | 'published_priority_list'
>;

export interface PlanScenarioSetupParams {
  /** The plan cycle to bind the new scenario to. */
  planCycleId: string;
  /** Used to seed the scenario's name ("<plan name> — Scenario"). */
  planName: string;
  /** The workspace — `planningEngineService`'s `projectId`. */
  workspaceId: string;
  sourceType: PlanScenarioSourceType;
  /** The item pool / collection / published list id to scope the engine to. */
  inputId: string;
  /** Display name for the scope badge and the landing confirmation. */
  inputName: string;
}

export type PlanScenarioSetupStepName = 'scenario' | 'scope_binding' | 'activate_scenario';

export interface PlanScenarioSetupStep {
  step: PlanScenarioSetupStepName;
  ok: boolean;
  error?: string;
}

export interface PlanScenarioSetupState {
  scenarioId: string | null;
  scopeBound: boolean;
  activated: boolean;
  steps: PlanScenarioSetupStep[];
}

/** True only when every step completed — the confirmation-banner condition. */
export function isPlanScenarioSetupComplete(state: PlanScenarioSetupState): boolean {
  return state.scenarioId !== null && state.scopeBound && state.activated;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Runs (or resumes) the scenario/scope/activation chain for a plan cycle.
 * Pass a prior `resume` state (e.g. from a failed attempt) to skip completed
 * steps — the "Finish setup" retry path.
 */
export async function setUpPlanScenario(
  params: PlanScenarioSetupParams,
  resume?: Partial<PlanScenarioSetupState>,
): Promise<PlanScenarioSetupState> {
  const steps: PlanScenarioSetupStep[] = [];
  let scenarioId = resume?.scenarioId ?? null;

  if (!scenarioId) {
    try {
      const defaults = usePlanningDefaultsStore.getState().defaults;
      const scenario = await createScenario(params.workspaceId, {
        name: `${params.planName} — Scenario`,
        planCycleId: params.planCycleId,
        planningMode: defaults.planningMode,
        planningWeeks: defaults.planningWeeks,
        capacityBufferPercent: defaults.bufferPct / 100,
        defaultWorkUnitHours: defaults.defaultWuHours,
        includeNotReady: false,
        includeBlocked: false,
      });
      scenarioId = scenario.id;
      steps.push({ step: 'scenario', ok: true });
    } catch (err) {
      steps.push({ step: 'scenario', ok: false, error: errorMessage(err) });
      return { scenarioId: null, scopeBound: false, activated: false, steps };
    }
  }

  let scopeBound = resume?.scopeBound ?? false;
  if (!scopeBound) {
    try {
      await bindScenarioInputSource(scenarioId, {
        type: params.sourceType,
        id: params.inputId,
        name: params.inputName,
      } as EngineInputSource);
      scopeBound = true;
      steps.push({ step: 'scope_binding', ok: true });
    } catch (err) {
      steps.push({ step: 'scope_binding', ok: false, error: errorMessage(err) });
      return { scenarioId, scopeBound: false, activated: false, steps };
    }
  }

  let activated = resume?.activated ?? false;
  if (!activated) {
    try {
      await activateScenario(scenarioId, params.workspaceId);
      activated = true;
      steps.push({ step: 'activate_scenario', ok: true });
    } catch (err) {
      steps.push({ step: 'activate_scenario', ok: false, error: errorMessage(err) });
      return { scenarioId, scopeBound, activated: false, steps };
    }
  }

  return { scenarioId, scopeBound, activated, steps };
}

/** The one confirmation line for a fully-set-up plan, everywhere it's shown. */
export function planScenarioReadyMessage(inputName: string): string {
  return `Scenario ready — scope set to ${inputName}`;
}
